import type { FunctionComponent } from 'react';
import { FormattedDate, FormattedNumber } from 'react-intl';

import { MethodType } from '@endeavour/payment-integration';

import { Button, ButtonSize } from '../../../shared/components/button/button';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { Icon, IconSize } from '../../../shared/components/icon/icon';
import { createStyleHelper } from '../../../shared/utils/class-names';
import { slugify } from '../../../shared/utils/strings';
import { ITransactionStatus } from '../../queries/cashier.graphql';

import styles from './receipt.module.scss';

const style = createStyleHelper(styles, 'receipt');

function getStatusIcon(status: ITransactionStatus): string {
    switch (status) {
        case ITransactionStatus.Completed:
            return 'check-circle';
        case ITransactionStatus.Failed:
        case ITransactionStatus.Declined:
        case ITransactionStatus.Rejected:
        case ITransactionStatus.Canceled:
            return 'times-circle';
        case ITransactionStatus.PendingAuthorization:
        case ITransactionStatus.PendingReversal:
        case ITransactionStatus.PendingValidation:
            return 'question-circle';
    }
    return 'times-circle';
}

export interface IReceiptProps {
    timestamp: number;
    transactionId: string;
    transactionAmount: number;
    currency: string;
    fee: number;
    feeCurrency: string;
    afterTransactionBalanceAmount?: number | null;
    transactionStatus: ITransactionStatus;
    method: MethodType;
    operationType?: string;
    error?: string;
}

export const Receipt: FunctionComponent<IReceiptProps> = ({
    timestamp,
    transactionId,
    transactionAmount,
    currency,
    fee,
    feeCurrency,
    afterTransactionBalanceAmount,
    transactionStatus,
    method,
    operationType,
    error,
}) => {
    const status = transactionStatus.toLocaleLowerCase();
    return (
        <div className={style()}>
            <div className={style('icon', [`${slugify(status)}`])}>
                <Icon name={getStatusIcon(transactionStatus)} size={IconSize.AUTO} />
            </div>
            <div className={style('row')}>
                <div className={style('label')}>
                    <FormattedMessage id="receipt.date.label" defaultMessage="Date" />
                </div>
                <div className={style('value', ['double'])}>
                    <FormattedDate value={timestamp} dateStyle="short" timeStyle="short" />
                </div>
            </div>
            <div className={style('row', ['transaction'])}>
                <div className={style('label')}>
                    <FormattedMessage
                        id="receipt.transaction-id.label"
                        defaultMessage="Transaction ID"
                    />
                </div>
                <div className={style('value', ['transaction'])}>{transactionId}</div>
            </div>
            <div className={style('row')}>
                <div className={style('label')}>
                    {method === MethodType.DEPOSIT && (
                        <FormattedMessage
                            id="receipt.amount-deposited.label"
                            defaultMessage="Deposit Transaction Amount "
                        />
                    )}
                    {method === MethodType.WITHDRAWAL && (
                        <FormattedMessage
                            id="receipt.amount-withdrawn.label"
                            defaultMessage="Withdrawal Transaction Amount "
                        />
                    )}
                </div>
                <div className={style('value')}>
                    <FormattedNumber
                        value={transactionAmount}
                        currency={currency}
                        style={'currency'}
                    />
                </div>
            </div>
            <div className={style('row')}>
                <div className={style('label')}>
                    <FormattedMessage id="receipt.fee.label" defaultMessage="Fee" />
                </div>
                <div className={style('value')}>
                    <FormattedNumber value={fee} currency={feeCurrency} style={'currency'} />
                </div>
            </div>
            {afterTransactionBalanceAmount ? (
                <div className={style('row')}>
                    <div className={style('label')}>
                        <FormattedMessage
                            id="receipt.final-balance.label"
                            defaultMessage="Balance after transaction"
                        />
                    </div>
                    <div className={style('value')}>
                        <FormattedNumber
                            value={afterTransactionBalanceAmount}
                            currency={currency}
                            style={'currency'}
                        />
                    </div>
                </div>
            ) : null}
            {error ? (
                <p className={style('error')}>
                    <FormattedMessage
                        id={`cashier.receipt.${slugify(operationType)}.error.${slugify(error)}`}
                        defaultMessage={error}
                    />
                </p>
            ) : null}
            <p className={style('extra')}>
                <FormattedMessage
                    id="receipt.extra-information"
                    defaultMessage="This transaction will appear as X."
                />
            </p>
            <div className={style('button')}>
                {transactionStatus === ITransactionStatus.Completed && (
                    <Button
                        className={style('button-item')}
                        size={ButtonSize.MEDIUM}
                        href="/sports"
                    >
                        <FormattedMessage
                            id="cashier.receipt.back-to-games"
                            defaultMessage="Bet Now"
                        />
                    </Button>
                )}
                {(transactionStatus === ITransactionStatus.Failed ||
                    transactionStatus === ITransactionStatus.Declined) && (
                    <Button
                        className={style('button-item')}
                        size={ButtonSize.MEDIUM}
                        href="/cashier/deposit/"
                    >
                        <FormattedMessage
                            id="cashier.receipt.back-to-deposit"
                            defaultMessage="Back To Deposit"
                        />
                    </Button>
                )}
                {(transactionStatus === ITransactionStatus.PendingAuthorization ||
                    transactionStatus === ITransactionStatus.PendingValidation ||
                    transactionStatus === ITransactionStatus.PendingReversal) && (
                    <Button
                        className={style('button-item')}
                        size={ButtonSize.MEDIUM}
                        onClick={() => window.location.reload()}
                    >
                        <FormattedMessage
                            id="cashier.receipt.refresh-page"
                            defaultMessage="Refresh Page"
                        />
                    </Button>
                )}
            </div>
        </div>
    );
};
